import { useSyncExternalStore } from "react";
import { registerSW } from "virtual:pwa-register";

interface ServiceWorkerStatus {
  needRefresh: boolean;
  offlineReady: boolean;
  registrationState: "unavailable" | "registering" | "registered" | "offline-ready" | "error";
}

interface ServiceWorkerRegistrationOptions {
  enabled?: boolean;
  serviceWorkerSupported?: boolean;
}

type UpdateServiceWorker = (reloadPage?: boolean) => Promise<void>;

const listeners = new Set<() => void>();
let status: ServiceWorkerStatus = {
  needRefresh: false,
  offlineReady: false,
  registrationState: "unavailable",
};
let updateServiceWorker: UpdateServiceWorker = () => Promise.resolve();

function emit(nextStatus: ServiceWorkerStatus): void {
  status = nextStatus;
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): ServiceWorkerStatus {
  return status;
}

export function registerAppServiceWorker({
  enabled = import.meta.env.PROD,
  serviceWorkerSupported = typeof navigator !== "undefined" && "serviceWorker" in navigator,
}: ServiceWorkerRegistrationOptions = {}): void {
  updateServiceWorker = () => Promise.resolve();

  if (!enabled || !serviceWorkerSupported) {
    emit({ needRefresh: false, offlineReady: false, registrationState: "unavailable" });
    return;
  }

  emit({ needRefresh: false, offlineReady: false, registrationState: "registering" });
  updateServiceWorker = registerSW({
    immediate: true,
    onNeedRefresh() {
      emit({
        ...status,
        needRefresh: true,
        registrationState:
          status.registrationState === "offline-ready" ? "offline-ready" : "registered",
      });
    },
    onOfflineReady() {
      emit({ ...status, offlineReady: true, registrationState: "offline-ready" });
    },
    onRegisteredSW(_serviceWorkerUrl, registration) {
      emit({
        ...status,
        registrationState: registration
          ? status.registrationState === "offline-ready"
            ? "offline-ready"
            : "registered"
          : "error",
      });
    },
    onRegisterError() {
      emit({ ...status, registrationState: "error" });
    },
  });
}

export function useServiceWorkerStatus(): ServiceWorkerStatus {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export async function applyServiceWorkerUpdate(): Promise<void> {
  await updateServiceWorker(true);
}

export function dismissOfflineReady(): void {
  emit({ ...status, offlineReady: false });
}
